/**
 * LGScreen1.tsx - Gain frame presentation (Unified Design)
 * Last updated: 2025-08-13 09:35:00
 * Updated by: JulianPolly
 */

import React, { useState, useEffect } from "react";
import { ScreenProps } from "../../SequenceController";
import { FormGroup, Slider, Icon } from "@blueprintjs/core";
import LGFraming from "./assets/LGFraming.png";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles } from "../../../../components/colors";

type InsuranceScreenClick = {
    sliderValue: number;
    millisecondsSinceFirstRender: number;
    bedingung: string;
};

type InsuranceScreenResult = {
    selections: Array<InsuranceScreenClick>;
};

export const InsuranceGainInitialResult: InsuranceScreenResult = {
    selections: [],
};

export const LGScreen1 = (props: ScreenProps) => {
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [slider, setSlider] = useState(2); // Mittelwert (0-4)
    const { setCanContinue, setResult } = props;
    const { t, language } = useLanguage();

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
    }, [setResult]);

    useEffect(() => {
        setCanContinue(true);
        setResult((result: InsuranceScreenResult) => {
            result.selections = [];
            result.selections.push({
                sliderValue: slider,
                millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp,
                bedingung: "Bedingung 1"
            });
            return result;
        });
    }, [slider, setCanContinue, setResult, firstRenderTimestamp]);

    const sliderLabels = language === 'en'
        ? [
            "very unlikely",
            "somewhat unlikely",
            "undecided",
            "somewhat likely",
            "very likely"
        ]
        : [
            "sehr unwahrscheinlich",
            "eher unwahrscheinlich",
            "unentschieden",
            "eher wahrscheinlich",
            "sehr wahrscheinlich"
        ];

    const title = t('experiments', 'LGFraming', 'screens', 'screen1', 'title') ||
        (language === 'en' ? "Smartphone Purchase – A Decision Awaits!" : "Smartphone-Kauf – Eine Entscheidung steht an!");

    const content = t('experiments', 'LGFraming', 'screens', 'screen1', 'content') ||
        (language === 'en'
            ? "You've saved for a long time and thoroughly researched various smartphone models. Now you're at the checkout in the store – your new, long-awaited device is already in front of you. Just before you pay, you're looking forward to finally holding it in your hands.\n\nWhen it's your turn, the employee asks if you would like to purchase insurance for your new smartphone. This insurance would cover damage from drops, broken glass, and other defects."
            : "Du hast lange gespart und dich gründlich über verschiedene Smartphone-Modelle informiert. Jetzt stehst du im Laden an der Kasse – dein neues, lang ersehntes Gerät liegt bereits vor dir. Kurz bevor du bezahlst, freust du dich schon darauf, es endlich in den Händen zu halten.\n\nAls du an der Reihe bist, fragt dich der Mitarbeiter, ob du eine Versicherung für dein neues Smartphone abschließen möchtest. Diese Versicherung würde Schäden durch Stürze, Glasbrüche und andere Defekte abdecken.");

    const employeeIntro = t('experiments', 'LGFraming', 'screens', 'screen1', 'employeeIntro') ||
        (language === 'en' ? "The employee says:" : "Der Mitarbeiter sagt:");

    const gainFrame = t('experiments', 'LGFraming', 'screens', 'screen1', 'gainFrame') ||
        (language === 'en'
            ? "\"If you purchase insurance, your phone is protected against most damages – for example, if you drop it once and the glass breaks. In that case, either the repair will be paid for or you'll quickly receive a replacement device.\""
            : "\"Wenn du eine Versicherung abschließt, ist dein Handy gegen die meisten Schäden abgesichert – zum Beispiel, wenn es dir einmal herunterfällt und das Glas kaputt geht. In dem Fall wird entweder die Reparatur bezahlt oder du erhältst schnell ein Ersatzgerät.\"");

    const question = t('experiments', 'LGFraming', 'screens', 'screen1', 'question') ||
        (language === 'en'
            ? "How likely are you to purchase insurance for your smartphone?"
            : "Wie wahrscheinlich ist es, dass du eine Versicherung für dein Smartphone abschließt?");

    const answerLabel = t('experiments', 'LGFraming', 'screens', 'screen1', 'answer') ||
        (language === 'en' ? "Your answer:" : "Deine Antwort:");

    const contentParagraphs = content.split("\n\n");

    return (
        <div className="experiment-container" style={commonStyles.container}>
            {/* Unified content card: header, story, image, slider */}
            <div style={commonStyles.card}>
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {title}
                </h2>
                <div style={{
                    ...commonStyles.paragraph,
                    textAlign: "justify", // blocksatz
                    textJustify: "inter-word"
                }}>
                    {contentParagraphs.map((paragraph: string, i: number) => (
                        <p key={i} style={{ margin: i === 0 ? "0 0 16px 0" : "16px 0 0 0" }}>
                            {paragraph}
                        </p>
                    ))}
                </div>
                <div style={{ textAlign: "center", margin: "40px 0 0 0" }}>
                    <img
                        src={LGFraming}
                        alt="Smartphone Framing Illustration"
                        style={{
                            maxWidth: 420,
                            width: "100%",
                            height: "auto",
                            borderRadius: 16,
                            boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)"
                        }}
                    />
                </div>
                {/* Mitarbeiter-Kommentar in Highlighting Box */}
                <div style={{
                    backgroundColor: colors.ocker,
                    border: `1px solid ${colors.ockerLight}`,
                    padding: "14px 20px",
                    borderRadius: 8,
                    margin: "32px 0 22px 0",
                    fontSize: 16,
                    fontWeight: 500,
                    color: colors.white,
                    display: "flex",
                    alignItems: "center",
                    gap: 12
                }}>
                    <Icon icon="person" style={{ marginRight: 9, fontSize: 18 }} />
                    <span>
                        <span style={{ fontWeight: 600 }}>{employeeIntro} </span>
                        <span style={{ fontStyle: "italic", fontWeight: 400 }}>{gainFrame}</span>
                    </span>
                </div>
                {/* Centered Question above slider */}
                <div style={{ textAlign: "center", margin: "40px 0 20px 0" }}>
                    <span style={{
                        color: colors.brillantBlue,
                        fontSize: 22,
                        fontWeight: 600,
                        textAlign: "center"
                    }}>{question}</span>
                </div>
                <FormGroup
                    label={undefined}
                    labelFor="slider-input"
                    style={{ marginTop: 0, marginBottom: 30 }}
                >
                    <div style={{ maxWidth: 540, margin: "0 auto" }}>
                        <Slider
                            min={0}
                            max={4}
                            stepSize={1}
                            labelStepSize={1}
                            value={slider}
                            onChange={setSlider}
                            labelRenderer={false}
                            showTrackFill={false}
                            intent={undefined}
                            className="no-track-slider"
                        />
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: 8,
                            gap: 4
                        }}>
                            {sliderLabels.map((label, idx) => (
                                <span key={idx} style={{
                                    fontSize: 13,
                                    width: 90,
                                    textAlign: "center",
                                    color: idx === slider ? colors.brillantBlue : colors.textMedium,
                                    fontWeight: idx === slider ? 600 : 400
                                }}>{label}</span>
                            ))}
                        </div>
                    </div>
                </FormGroup>
                <p style={{
                    textAlign: "center",
                    marginTop: 20,
                    color: colors.textDark,
                    fontSize: 16
                }}>
                    {answerLabel} <b style={{ color: colors.brillantBlue }}>{sliderLabels[slider]}</b>
                </p>
            </div>
        </div>
    );
};